import { useNotify } from '@/components/snackbar';
import { CONTENT_DELIVERY_OPTIONS } from '@/consts/product-settings';
import useTableFilter from '@/hooks/useTableFilter';
import { resolveDuplicateProductNotice } from '@/sections/@dashboard/products/duplicate-product/duplicateProduct.utils';
import { CACHE_TTL } from '@/services/constants';
import {
  createProductService,
  duplicateProductService,
  getContentDeliveries,
  getProductCategoriesService,
  getProductsService
} from '@/services/products';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { createContext, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

const DEFAULT_PAGE = 1;

export const ProductsContext = createContext({
  products: [],
  isFetchingProducts: false,
  productsError: false,
  categories: [],
  isFetchingCategories: false,
  createProduct: () => { },
  duplicateProduct: () => { },
  isDuplicatingProduct: false,
  duplicatingProductId: null,
  table: {},
  total: 0,
  status: 'active',
  type: 'unique,subscription',
  currency: 'all',
  nfse: 'all',
  search: '',
  setStatus: () => { },
  setType: () => { },
  setCurrency: () => { },
  setNfse: () => { },
  setSearch: () => { },
  contentDeliveriesOptions: [],
  isLoadingContentDeliveriesOptions: false,
});

const ProductsProvider = ({
  children,
  enableProductsQuery = true,
  enableContentDeliveriesQuery = false,
  enableCategoriesQuery = false
}) => {
  const table = useTableFilter({
    defaultCurrentPage: DEFAULT_PAGE,
    tableProps: {
      defaultRowsPerPage: 8,
    }
  });

  const [status, setStatus] = useState('active');
  const [type, setType] = useState('unique,subscription');
  const [currency, setCurrency] = useState('all');
  const [nfse, setNfse] = useState('all');

  const { data: categories = [], isFetching: isFetchingCategories } = useQuery({
    queryKey: ['categories'],
    queryFn: getProductCategoriesService,
    enabled: enableCategoriesQuery,
    initialData: [],
  });

  const handleChangeType = useCallback(
    (newType) => {
      setType(newType);
      table.setPage(DEFAULT_PAGE);
    },
    [table]
  );

  const handleChangeCurrency = useCallback(
    (newCurrency) => {
      setCurrency(newCurrency);
      table.setPage(DEFAULT_PAGE);
    },
    [table]
  );

  const notify = useNotify();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    data: products,
    isFetching: isFetchingProducts,
    isError: productsError,
    refetch: refetchProducts,
  } = useQuery(
    ['products', status, table.search, table.page, table.rowsPerPage, type, currency, nfse],
    () => getProductsService({
      search: table.search,
      status,
      page: table.page,
      limit: table.rowsPerPage,
      type,
      currency,
      nfse,
      ordering: '-createdAt',
    }),

    {
      enabled: enableProductsQuery,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      // Segura a página anterior na tela enquanto a próxima busca carrega. A chave só
      // gira por apresentação (página, busca, filtros): trocar de conta recarrega o app.
      keepPreviousData: true,
      onError: (error) => {
        if (error.response?.status === 404) {
          navigate('/dashboard/home');
        }
      },
    }
  );

  const { mutateAsync: createProduct } = useMutation({
    mutationFn: createProductService,
    onSuccess: ({ id }) => {
      queryClient.invalidateQueries('products');

      notify.success('Produto criado com sucesso');

      navigate(`/dashboard/products/${id}/edit?tab=general`);
    },
    onError: (error) => {
      notify.error(error, { dataTestId: 'create-product-error' });
    },
  });

  const {
    mutateAsync: duplicateProduct,
    isLoading: isDuplicatingProduct,
    variables: duplicatingProductId,
  } = useMutation({
    mutationFn: duplicateProductService,
    onSuccess: (duplicatedProduct) => {
      queryClient.invalidateQueries('products');

      const { variant, message, options } = resolveDuplicateProductNotice(duplicatedProduct);
      notify[variant](message, options);

      // navigate(`/dashboard/products/${id}/edit?tab=general`);
    },
  });

  const {
    data: contentDeliveriesOptions = [],
    isLoading: isLoadingContentDeliveriesOptions
  } = useQuery(
    {
      queryKey: ['content-deliveries'],
      queryFn: getContentDeliveries,
      enabled: enableContentDeliveriesQuery,
      select(result) {
        return result?.map((item) => {
          const content = CONTENT_DELIVERY_OPTIONS.find(({ value }) => value === item.type);

          if (content)
            return {
              ...content,
              beta: content?.beta || false,
              noSupport: content.noSupport || false,
              recommended: content.recommended || false,
            };

          return {
            title: item?.name,
            description: '',
            value: item?.type,
            beta: false,
            noSupport: false,
            recommended: false,
            icon: (
              <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="40" height="40" rx="20" fill="#212B36" />
                <circle
                  cx="20"
                  cy="20"
                  r="8"
                  stroke="#36B37E"
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14 26 Q20 30, 26 26"
                  stroke="#36B37E"
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ),
          };
        });
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: CACHE_TTL,
      cacheTime: CACHE_TTL,
    }
  );

  // Depois de uma alteração em lote toda página e todo filtro em cache ficam velhos, não só o
  // que está na tela; e o produto aberto depois (`['product', {id}]`) também.
  const reloadProducts = useCallback(
    (changedProductIds = []) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['products'] }),
        ...changedProductIds.map((id) =>
          queryClient.invalidateQueries({ queryKey: ['product', { id }] })
        ),
      ]),
    [queryClient]
  );

  const value = useMemo(
    () => ({
      products: products?.products || [],
      categories,
      isFetchingCategories,
      createProduct,
      duplicateProduct,
      isDuplicatingProduct,
      duplicatingProductId: isDuplicatingProduct ? duplicatingProductId : null,
      status,
      setStatus,
      type,
      setType: handleChangeType,
      currency,
      setCurrency: handleChangeCurrency,
      nfse,
      setNfse,
      isFetchingProducts,
      table,
      count: products?.count || 0,
      contentDeliveriesOptions,
      isLoadingContentDeliveriesOptions,
      productsError,
      refetchProducts,
      reloadProducts,
    }),
    [
      products,
      categories,
      isFetchingCategories,
      createProduct,
      duplicateProduct,
      isDuplicatingProduct,
      duplicatingProductId,
      status,
      type,
      currency,
      nfse,
      isFetchingProducts,
      table,
      contentDeliveriesOptions,
      isLoadingContentDeliveriesOptions,
      handleChangeType,
      handleChangeCurrency,
      productsError,
      refetchProducts,
      reloadProducts,
    ]
  );

  return <ProductsContext.Provider value={value}>{children}</ProductsContext.Provider>;
};

ProductsProvider.propTypes = {
  children: PropTypes.node.isRequired,
  enableCategoriesQuery: PropTypes.bool,
  enableContentDeliveriesQuery: PropTypes.bool,
  enableProductsQuery: PropTypes.bool,
};

export default ProductsProvider;
